import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Mail,
  ArrowLeft,
  Eye,
  EyeOff,
  RefreshCw,
  Settings
} from 'lucide-react';
import {
  useEmailSettings,
  useCreateEmailSettings,
  useUpdateEmailSettings,
  useDiagnoseMail,
  type EmailSettings
} from '@/api-services/hooks/useSettings';

interface EmailSettingsForm {
  id?: number;
  email: string;
  name: string;
  password: string;
  testEmail: string;
}

const EmailSettingsPage = () => {
  const can = usePermissionCheck();
  // Fetch existing email settings
  const { loading: isLoadingSettings } = useEmailSettings({
    onSuccess: (data) => {
      if (data) {
        setFormData({
          id: data.id,
          email: data.email || '',
          name: data.name || '',
          password: data.password || '',
          testEmail: ''
        });
      }
    },
    onError: (error) => {
      console.error('Failed to fetch email settings:', error);
    }
  });

  const createEmailSettings = useCreateEmailSettings({
    onSuccess: () => {
      toast.success('Email settings saved successfully');
    },
    onError: (error) => {
      toast.error(`Failed to save email settings: ${error.message}`);
    }
  });

  const updateEmailSettings = useUpdateEmailSettings({
    onSuccess: () => {
      toast.success('Email settings updated successfully');
    },
    onError: (error) => {
      toast.error(`Failed to update email settings: ${error.message}`);
    }
  });

  const diagnoseMail = useDiagnoseMail({
    onSuccess: (data) => {
      toast.success(data.message || 'Test email sent successfully');
    },
    onError: (error) => {
      toast.error(`Failed to send test email: ${error.message}`);
    }
  });

  const [formData, setFormData] = useState<EmailSettingsForm>({
    email: '',
    name: '',
    password: '',
    testEmail: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiagnosing, setIsDiagnosing] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.email || !formData.name || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      if (formData.id) {
        // Update existing settings
        await updateEmailSettings.mutate({
          id: formData.id,
          email: formData.email,
          name: formData.name,
          password: formData.password,
        });
      } else {
        // Create new settings
        const result:any = await createEmailSettings.mutate({
          email: formData.email,
          name: formData.name,
          password: formData.password,
        });
         // Extract ID from string response
        const match = result?.match(/\((\d+)\)/);
        if (match) {
          const extractedId = Number(match[1]);
          setFormData(prev => ({ ...prev, id: extractedId }));
        } else {
          console.error("ID not found in response");
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = () => {
    setFormData({
      id: formData.id, // Keep the id for updates
      email: '',
      name: '',
      password: '',
      testEmail: ''
    });
    toast.info('Form cleared');
  };

  const handleDiagnoseTestMail = async () => {
    if (!formData.testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    setIsDiagnosing(true);
    try {
      await diagnoseMail.mutate({
        toEmail: formData.testEmail
      });
    } finally {
      setIsDiagnosing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
              <Settings className="w-7 h-7 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Notifications</h1>
              <p className="text-muted-foreground">Configure email and SMS settings</p>
            </div>
          </div>

          {/* Back Button */}
          <Link to="/admin/dashboard" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>

          {/* Email Settings Card */}
          <Card className="border-border/50 shadow-elegant">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-xl">
                <Mail className="w-5 h-5 text-primary" />
                <span>Email Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingSettings ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-3 text-muted-foreground">Loading email settings...</span>
                </div>
              ) : (
                <form onSubmit={handleSave} className="space-y-6">
                {/* Section Title */}
                <div className="border-b border-border pb-3 mb-6">
                  <h3 className="text-lg font-semibold text-foreground">Email Information</h3>
                </div>

                {/* Form Fields Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center space-x-1">
                      <span>Email</span>
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="bg-background"
                    />
                  </div>

                  {/* Name Field */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center space-x-1">
                      <span>Name</span>
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="bg-background"
                    />
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <Label htmlFor="password" className="flex items-center space-x-1">
                      <span>Password</span>
                      <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="bg-background pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Test Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="testEmail">Test Email</Label>
                    <Input
                      id="testEmail"
                      name="testEmail"
                      type="email"
                      placeholder="Test Email"
                      value={formData.testEmail}
                      onChange={handleInputChange}
                      className="bg-background"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                {can('Edit Distance & Wait Settings') && (
                  <div className="flex flex-wrap items-center gap-3 pt-4">
                    <Button
                      type="submit"
                      variant="default"
                      className="bg-primary hover:bg-primary/90 px-6"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save'
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleClear}
                      className="px-6"
                    >
                      Clear
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDiagnoseTestMail}
                      disabled={isDiagnosing}
                      className="px-6"
                    >
                      {isDiagnosing ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        'Diagnose Test Mail'
                      )}
                    </Button>
                  </div>
                )}
              </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmailSettingsPage;
